import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationService } from "notifications/services";
import { useBoundStore } from "store";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (tenantId?: string, organizationId?: string) =>
    [...notificationKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "list"] as const,
  unreadCount: (tenantId?: string, organizationId?: string) =>
    [...notificationKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "unread-count"] as const,
};

/**
 * Fetches notifications for an organization.
 */
export function useNotifications(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: notificationKeys.list(tenant?.id, organizationId),
    queryFn: () => notificationService.getNotifications(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Fetches the unread notification count for an organization.
 */
export function useUnreadNotificationCount(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: notificationKeys.unreadCount(tenant?.id, organizationId),
    queryFn: () => notificationService.getUnreadCount(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Mutation to mark a single notification as read.
 * Invalidates notifications queries on success.
 */
export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      organizationId,
      notificationId,
    }: {
      tenantId: string;
      organizationId: string;
      notificationId: string;
    }) => notificationService.markAsRead(tenantId, organizationId, notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mutation to mark all notifications as read.
 * Invalidates notifications queries on success.
 */
export function useMarkAllNotificationsReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      organizationId,
    }: {
      tenantId: string;
      organizationId: string;
    }) => notificationService.markAllAsRead(tenantId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
